import { Signal } from "../core/Signal.js";
import { IPoint3dValue, Point3d } from "../geom/Point3d.js";
import { Calc } from "../math/Calc.js";
import { object } from "../util/ObjectUtil.js";
import { Actor, ActorNonOptions, ActorOptions, Pointer } from "./Actor.js";

export interface IOrbitCamera {
    lookAt(x: number, y: number, z: number): void;
    position: IPoint3dValue;
    translateOnAxis?: any;
}

export interface OrbitOptions extends ActorOptions {
    radius?: number;
    origin?: IPoint3dValue;
    damping?: number;
    dragSpeed?: number;
    offsetY?: number;
    invertX?: boolean;
    invertY?: boolean;
    allowInterrupt?: boolean;
    minRadius?: number;
    maxRadius?: number;
    zoom?: boolean;
}

export class Orbit extends Actor<Pointer, OrbitOptions> implements OrbitOptions {
    public static DEFAULT: OrbitOptions = {
        radius: 50,
        minRadius: 5,
        maxRadius: 300,
        zoom: false,
        origin: { x: 0, y: 0, z: 0 },
        damping: 0.2,
        dragSpeed: 0.3,
        offsetY: 0,
        invertX: false,
        invertY: false,
        allowInterrupt: true
    };

    public onChange = Signal.create();
    public onStartOrbit = Signal.create();
    public onEnd = Signal.create();

    public radius: number;
    public minRadius: number;
    public maxRadius: number;
    public zoom: boolean;
    public origin: IPoint3dValue;
    public damping: number;
    public dragSpeedX: number;
    public dragSpeedY: number;
    public offsetY: number;
    public invertX: boolean;
    public invertY: boolean;
    public allowInterrupt: boolean;

    private _radius: number = 0;
    private _lat: number = 0;
    private _lng: number = 0;
    private _latCurrent: number = 0;
    private _lngCurrent: number = 0;
    private _camera: IOrbitCamera;
    private _dragX: number = 0;
    private _dragY: number = 0;
    private _tweening: boolean = false;
    private _offsetY: number = 0;
    private _started: boolean = false;

    constructor(camera: IOrbitCamera, element: HTMLElement, options: OrbitOptions = null) {
        super(null, ActorNonOptions);

        if (options !== ActorNonOptions)
            this.setOptions(options);

        this.element = element;

        this._camera = camera;
        this.radius = Point3d.distance(this.origin, this._camera.position);
        this._radius = this.radius;

        var refresh = (): void => {
            if (this.count === 0)
                return;
            var pt = this.center;
            this._dragX = pt.x;
            this._dragY = pt.y;
        };

        var scaling = 0;
        this.onDown.add(() => {
            refresh();
            if (this._tweening) {
                gsap.killTweensOf(this);
                this._tweening = false;
            }
            else if (this.zoom && this.pointers.length > 1) {
                scaling = this.distance;
            }
        });

        this.onMove.add((): void => {
            var pt = this.center;
            var dx = pt.x - this._dragX;
            var dy = pt.y - this._dragY;
            this._dragX = pt.x;
            this._dragY = pt.y;

            if (this.invertX)
                dx *= -1;
            if (this.invertY)
                dy *= -1;
            this._lng -= dx * this.dragSpeedX;
            this.lat = this._lat + dy * this.dragSpeedY;

            if (this.zoom && this.pointers.length > 1) {
                var newScaling = this.distance;
                var scale = newScaling - scaling;
                this.radius = Calc.clamp(this.radius - scale * 0.5, this.minRadius, this.maxRadius);
                scaling = newScaling;
            }
        });

        this.onUp.add(refresh);

        var updateZoom = (e: Event, delta: number) => {
            if (!this.zoom)
                return;
            this.radius = Calc.clamp(this.radius + delta * 0.5, this.minRadius, this.maxRadius);
            console.log(this.radius);
            e.preventDefault();
        };
        this.element.addEventListener("mousewheel", (e: any) => updateZoom(e, -(e.wheelDelta / 40)));
        this.element.addEventListener("DOMMouseScroll", (e: any) => updateZoom(e, (<any>e).detail));

        this.updateCamera();
    }

    public setOptions(options: OrbitOptions) {
        options = object.merge({}, Orbit.DEFAULT, options);
        this.radius = options.radius;
        this.minRadius = options.minRadius;
        this.maxRadius = options.maxRadius;
        this.zoom = options.zoom;
        this.origin = options.origin;
        this.damping = options.damping;
        this.dragSpeedX = options.dragSpeed;
        this.dragSpeedY = options.dragSpeed;
        this.offsetY = options.offsetY;
        this.invertX = options.invertX;
        this.invertY = options.invertY;
        this.allowInterrupt = options.allowInterrupt;
        super.setOptions(options);
    }

    public get latCurrent(): number {
        return this._latCurrent;
    }

    public get lngCurrent(): number {
        return ((this._lngCurrent + 180) % 360) - 180;
    }

    public get lat(): number {
        return this._lat;
    }

    public set lat(lat: number) {
        this._lat = Calc.clamp(lat, -90, 90);
    }

    public get lng(): number {
        return ((this._lng + 180) % 360) - 180;
    }

    public set lng(lng: number) {
        //Clamp the input
        lng = Calc.clamp(lng, -180, 180);

        //Make the longitude between -180 and 180
        this._lngCurrent = ((this._lngCurrent + 180) % 360) - 180;

        //Short rotation
        var delta: number = (lng - this._lngCurrent) % 360;
        if (delta != delta % 180)
            delta = (delta < 0) ? delta + 360 : delta - 360;
        this._lng = this._lngCurrent + delta;
    }

    public tween(lat: number, lng: number, duration: number = 1, allowInterrupt: boolean = this.allowInterrupt): void {
        this._tweening = true;

        if (allowInterrupt) {
            //TODO: maybe clear pointers
            // this.cancel();
            this._pointers = [];
        } else {
            var before = this.enabled;
            this.enabled = false;
        }

        gsap.to(this, {
            duration: duration,
            directionalRotation: { _lat: lat, _lng: lng + "_short" },
            onComplete: () => {
                //Fixes jumping camera.
                this.lng = this._lng;
                this.updateCamera();

                //Remove tweening flag and re-enable dragging.
                this._tweening = false;
                if (!allowInterrupt)
                    this.enabled = before;
            }
        });
    }

    public lookAt(x: number, y: number, z: number, duration: number = 1, allowInterrupt: boolean = this.allowInterrupt): void {
        var len = Math.sqrt(x * x + y * y + z * z);
        var ez = Calc.clamp(y / Math.max(len, 0.0001), -1, 1);
        var lat = Math.asin(ez) * 180 / Math.PI;
        var lng = Math.atan2(-z, x) * 180 / Math.PI;
        this.tween(lat, lng, duration, allowInterrupt);
    }

    public update(force: boolean = false): boolean {
        //Check for trivial case: nothing to do!
        if (!force && this._radius === this.radius && this._lat === this._latCurrent && this._lng === this._lngCurrent && this._offsetY === this.offsetY) {
            if (this._started) {
                this.onEnd.dispatch();
                this._started = false;
            }
            return false;
        }

        this.updateCamera();

        if (!this._started) {
            this.onStartOrbit.dispatch();
            this._started = true;
        }

        return true;
    }

    private updateCamera(): void {
        //Disable damping during tween.
        var damping = this._tweening ? 1 : this.damping;

        //Remove very small deltas.
        var deltaLat = (this._lat - this._latCurrent);
        if (Math.abs(deltaLat) < 0.01) {
            deltaLat = 0;
            this._latCurrent = this._lat;
        }
        var deltaLng = (this._lng - this._lngCurrent);
        if (Math.abs(deltaLng) < 0.01) {
            deltaLng = 0;
            this._lngCurrent = this._lng;
        }

        //Get current latitutde/longitude.
        var lat = Calc.clamp(this._latCurrent + deltaLat * damping, -90, 90);
        var lng = this._lngCurrent + deltaLng * damping;
        this._latCurrent = lat;
        this._lngCurrent = lng;

        //From latitutde/longitude to x/y/z.
        var lambda = (lng + 180) * Math.PI / 180;
        var phi = lat * Math.PI / 180;
        var cosPhi = Math.cos(phi);
        var position = this._camera.position;
        this._radius = this.radius;
        position.x = this.origin.x + this._radius * -cosPhi * Math.cos(lambda);
        position.y = this.origin.y + this._radius * Math.sin(phi);
        position.z = this.origin.z + this._radius * cosPhi * Math.sin(lambda);

        //Look at origin (y-axis up).
        this._camera.lookAt(this.origin.x, this.origin.y, this.origin.z);

        //Offset the camera. Change the center of the camera.
        if (this.offsetY) {
            this._camera.translateOnAxis({ x: 0, y: 1, z: 0 }, this.offsetY);
            this._offsetY = this.offsetY;
        }

        this.onChange.dispatch();
    }
}